/* Public "Set your password" page the staff invite email links to.
   Flow: read ?token= from the URL → validate via /rbac/invite-info (greet by
   name) → user sets a password → /rbac/accept-invite activates the account →
   redirect to login. Reuses the Login page styling for visual consistency. */

import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import styles from "../login/Login.module.css";
import InputText from "../../component/inputText/InputText";
import Button from "../../component/button/Button";
import { getInviteInfo, acceptInvite } from "../../rbac/rbacService";
import { showToast } from "../../utils/Toastify/Toastify";
import { useTenant } from "../../context/TenantContext";
import useLocalStoreClear from "../../hooks/useLocalStoreClear";

function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { logo } = useTenant(); // tenant logo, else env/bundled fallback
  const { clearLocalStorage } = useLocalStoreClear();
  const token = searchParams.get("token") || "";

  const [checking, setChecking] = useState(true);
  const [invitee, setInvitee] = useState(null); // { name, email }
  const [invalid, setInvalid] = useState("");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);

  // Validate the token on load so we can greet the user (or show an error).
  useEffect(() => {
    let active = true;
    (async () => {
      if (!token) {
        setInvalid("This invitation link is missing its token.");
        setChecking(false);
        return;
      }
      try {
        const info = await getInviteInfo(token);
        if (active) setInvitee(info);
      } catch (err) {
        if (active) setInvalid(err.message || "This invitation is invalid or has expired.");
      } finally {
        if (active) setChecking(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  const handleSubmit = async () => {
    if (!password || password.length < 8) {
      showToast("Password must be at least 8 characters.", "warning");
      return;
    }
    if (password !== confirm) {
      showToast("Passwords do not match.", "warning");
      return;
    }
    try {
      setSaving(true);
      await acceptInvite(token, password);
      // Auth state lives in localStorage, which every tab/window of this
      // origin shares (incognito windows share one store too). If another
      // account — e.g. a creator — is still signed in there, the login page's
      // PublicRoute would see that token and bounce the invitee straight into
      // that other account's dashboard. Sign it out first so the invitee
      // always lands on login and signs in as the invited account.
      clearLocalStorage();
      window.dispatchEvent(new Event("storage"));
      showToast(
        invitee?.isReset
          ? "Password reset successfully. Please log in."
          : "Account activated. Please log in.",
        "success"
      );
      navigate("/");
    } catch (err) {
      showToast(
        err.message ||
          (invitee?.isReset
            ? "Failed to reset password."
            : "Failed to accept invitation."),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.container}>
      <img
        src={logo}
        alt="Logo"
        className={styles.logo}
      />

      <div className={styles.contain}>
        {checking ? (
          <p className={styles.loginText}>Validating your invitation…</p>
        ) : invalid ? (
          <>
            <p className={styles.loginText}>Invitation problem</p>
            <p style={{ color: "#c45b6d", textAlign: "center", margin: "8px 0 16px" }}>
              {invalid}
            </p>
            <Button onClick={() => navigate("/")}>Go to login</Button>
          </>
        ) : (
          <>
            <p className={styles.loginText}>
              {invitee?.isReset
                ? "Reset your password"
                : `Welcome${invitee?.name ? `, ${invitee.name}` : ""}`}
            </p>
            <p style={{ textAlign: "center", margin: "4px 0 16px", color: "#8a8a8a" }}>
              {invitee?.isReset
                ? "Choose a new password"
                : "Set a password to activate your account"}
              {invitee?.email ? ` (${invitee.email})` : ""}.
            </p>
            <div className={styles.inputGroup}>
              <div className={styles.passwordWrapper}>
                <InputText
                  type={showPwd ? "text" : "password"}
                  name="password"
                  placeholder="New password (min. 8 characters)"
                  className={styles.inputBox}
                  value={password}
                  variant="dark"
                  onChange={(e) => setPassword(e.target.value)}
                  showToggle={false}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                />
                <span
                  className={styles.eyeIcon}
                  onClick={() => setShowPwd((v) => !v)}
                >
                  {showPwd ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>

              <InputText
                type={showPwd ? "text" : "password"}
                name="confirm"
                placeholder="Confirm password"
                className={styles.inputBox}
                value={confirm}
                variant="dark"
                onChange={(e) => setConfirm(e.target.value)}
                showToggle={false}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              />

              <Button onClick={handleSubmit} loading={saving ? true : false}>
                {invitee?.isReset ? "Reset password" : "Activate account"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AcceptInvite;
